/**
 * Edit Rubric Page
 *
 * Edit a draft rubric. Only draft rubrics can be edited.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../../lib/api';

import { RequestPanel } from '../../components/RequestPanel';
import {
  validateRubricForm,
  generateFormId,
  type RubricFormData,
  type DimensionFormData,
  type RuleFormData,
} from '../../lib/trainer-validation';
import type { ApiResult, Rubric } from '../../lib/types';

const emptyRule = (): RuleFormData => ({
  id: generateFormId(),
  type: 'keyword',
  pattern: '',
  score: 1,
  required: false,
  caseSensitive: false,
});

const emptyDimension = (): DimensionFormData => ({
  id: generateFormId(),
  name: '',
  description: '',
  weight: 1,
  rules: [emptyRule()],
});

export function EditRubric() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [rubric, setRubric] = useState<Rubric | null>(null);
  const [formData, setFormData] = useState<RubricFormData | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<ApiResult<Rubric> | null>(null);
  const [errors, setErrors] = useState<string[]>([]);

  const loadRubric = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    const res = await apiClient.getRubric(id);
    setResult(res);
    if (res.response.data) {
      const r = res.response.data;
      setRubric(r);
      setFormData({
        name: r.name,
        description: r.description || '',
        passingScore: r.passingScore,
        dimensions: r.dimensions.map((dim) => ({
          id: dim.id,
          name: dim.name,
          description: dim.description || '',
          weight: dim.weight,
          rules: dim.rules.map((rule) => ({
            id: rule.id,
            type: rule.type,
            pattern: rule.pattern,
            score: rule.score,
            required: rule.required,
            caseSensitive: rule.caseSensitive || false,
          })),
        })),
      });
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    loadRubric();
  }, [loadRubric]);

  const validate = (): boolean => {
    if (!formData) return false;
    const errs = validateRubricForm(formData);
    setErrors(errs);
    return errs.length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData || !id || !validate()) return;

    setSaving(true);
    setResult(null);

    const res = await apiClient.updateRubric(id, {
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
      passingScore: formData.passingScore,
      dimensions: formData.dimensions.map((dim) => ({
        id: dim.id,
        name: dim.name.trim(),
        description: dim.description.trim() || undefined,
        weight: dim.weight,
        rules: dim.rules.map((rule) => ({
          id: rule.id,
          type: rule.type,
          pattern: rule.pattern.trim(),
          score: rule.score,
          required: rule.required,
          caseSensitive: rule.caseSensitive,
        })),
      })),
    });

    setResult(res);
    setSaving(false);

    if (res.response.data) {
      navigate(`/console/trainer/rubrics/${id}`);
    }
  };

  const updateDimension = (index: number, updates: Partial<DimensionFormData>) => {
    if (!formData) return;
    setFormData({
      ...formData,
      dimensions: formData.dimensions.map((d, i) => (i === index ? { ...d, ...updates } : d)),
    });
  };

  const addDimension = () => {
    if (!formData) return;
    setFormData({
      ...formData,
      dimensions: [...formData.dimensions, emptyDimension()],
    });
  };

  const removeDimension = (index: number) => {
    if (!formData) return;
    setFormData({
      ...formData,
      dimensions: formData.dimensions.filter((_, i) => i !== index),
    });
  };

  const updateRule = (dimIndex: number, ruleIndex: number, updates: Partial<RuleFormData>) => {
    if (!formData) return;
    setFormData({
      ...formData,
      dimensions: formData.dimensions.map((d, i) =>
        i === dimIndex
          ? {
              ...d,
              rules: d.rules.map((r, j) => (j === ruleIndex ? { ...r, ...updates } : r)),
            }
          : d
      ),
    });
  };

  const addRule = (dimIndex: number) => {
    if (!formData) return;
    setFormData({
      ...formData,
      dimensions: formData.dimensions.map((d, i) =>
        i === dimIndex ? { ...d, rules: [...d.rules, emptyRule()] } : d
      ),
    });
  };

  const removeRule = (dimIndex: number, ruleIndex: number) => {
    if (!formData) return;
    setFormData({
      ...formData,
      dimensions: formData.dimensions.map((d, i) =>
        i === dimIndex ? { ...d, rules: d.rules.filter((_, j) => j !== ruleIndex) } : d
      ),
    });
  };

  if (loading || !formData) {
    return (
      <div>
        <h1>Edit Rubric</h1>
        <p className="text-muted">Loading...</p>
      </div>
    );
  }

  if (rubric && rubric.status !== 'draft') {
    return (
      <div>
        <div className="flex items-center gap-4 mb-4">
          <Link to={`/console/trainer/rubrics/${id}`} style={{ color: '#666' }}>
            &larr; Back to Rubric
          </Link>
        </div>
        <h1>Edit Rubric</h1>
        <div className="error-box">Only draft rubrics can be edited. This rubric is {rubric.status}.</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-4 mb-4">
        <Link to={`/console/trainer/rubrics/${id}`} style={{ color: 'var(--text-tertiary)' }}>
          &larr; Back to Rubric
        </Link>
      </div>

      <h1>Edit Rubric</h1>

      {errors.length > 0 && (
        <div className="error-box" style={{ marginBottom: 16 }}>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {errors.map((err, i) => (
              <li key={i}>{err}</li>
            ))}
          </ul>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Basic Info */}
        <div className="card" style={{ marginBottom: 16 }}>
          <h2>Basic Information</h2>
          <div className="form-group">
            <label className="label">Name *</label>
            <input
              type="text"
              className="input"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label className="label">Description</label>
            <textarea
              className="input"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
            />
          </div>
          <div className="form-group" style={{ maxWidth: 200 }}>
            <label className="label">Passing Score (0-100) *</label>
            <input
              type="number"
              className="input"
              value={formData.passingScore}
              onChange={(e) => setFormData({ ...formData, passingScore: Number(e.target.value) })}
              min={0}
              max={100}
            />
          </div>
        </div>

        {/* Dimensions */}
        {formData.dimensions.map((dim, dimIndex) => (
          <div key={dimIndex} className="card" style={{ marginBottom: 16 }}>
            <div className="flex justify-between items-center" style={{ marginBottom: 16 }}>
              <h2>Dimension {dimIndex + 1}</h2>
              {formData.dimensions.length > 1 && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => removeDimension(dimIndex)}
                  style={{ fontSize: 12, padding: '4px 12px', color: 'var(--semantic-error)' }}
                >
                  Remove Dimension
                </button>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 16 }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="label">Name *</label>
                <input
                  type="text"
                  className="input"
                  value={dim.name}
                  onChange={(e) => updateDimension(dimIndex, { name: e.target.value })}
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="label">Description</label>
                <input
                  type="text"
                  className="input"
                  value={dim.description}
                  onChange={(e) => updateDimension(dimIndex, { description: e.target.value })}
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0, width: 100 }}>
                <label className="label">Weight *</label>
                <input
                  type="number"
                  className="input"
                  value={dim.weight}
                  onChange={(e) => updateDimension(dimIndex, { weight: Number(e.target.value) })}
                  min={0.1}
                  step={0.1}
                />
              </div>
            </div>

            {/* Rules */}
            <div style={{ marginTop: 16 }}>
              <h3 style={{ marginBottom: 12 }}>Rules</h3>
              <table className="table" style={{ fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={{ width: 100 }}>Type</th>
                    <th>Pattern</th>
                    <th style={{ width: 80 }}>Score</th>
                    <th style={{ width: 80 }}>Required</th>
                    <th style={{ width: 80 }}>Case</th>
                    <th style={{ width: 50 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {dim.rules.map((rule, ruleIndex) => (
                    <tr key={ruleIndex}>
                      <td>
                        <select
                          className="input"
                          value={rule.type}
                          onChange={(e) =>
                            updateRule(dimIndex, ruleIndex, {
                              type: e.target.value as 'keyword' | 'regex',
                            })
                          }
                          style={{ padding: '4px 8px', fontSize: 13 }}
                        >
                          <option value="keyword">keyword</option>
                          <option value="regex">regex</option>
                        </select>
                      </td>
                      <td>
                        <input
                          type="text"
                          className="input mono"
                          value={rule.pattern}
                          onChange={(e) => updateRule(dimIndex, ruleIndex, { pattern: e.target.value })}
                          style={{ padding: '4px 8px', fontSize: 13 }}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          className="input"
                          value={rule.score}
                          onChange={(e) =>
                            updateRule(dimIndex, ruleIndex, { score: Number(e.target.value) })
                          }
                          style={{ padding: '4px 8px', fontSize: 13 }}
                        />
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={rule.required}
                          onChange={(e) =>
                            updateRule(dimIndex, ruleIndex, { required: e.target.checked })
                          }
                        />
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={rule.caseSensitive}
                          onChange={(e) =>
                            updateRule(dimIndex, ruleIndex, { caseSensitive: e.target.checked })
                          }
                        />
                      </td>
                      <td>
                        {dim.rules.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeRule(dimIndex, ruleIndex)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: 'var(--semantic-error)',
                              cursor: 'pointer',
                              fontSize: 16,
                            }}
                          >
                            &times;
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => addRule(dimIndex)}
                style={{ marginTop: 8, fontSize: 12, padding: '4px 12px' }}
              >
                + Add Rule
              </button>
            </div>
          </div>
        ))}

        <button
          type="button"
          className="btn btn-secondary"
          onClick={addDimension}
          style={{ marginBottom: 16 }}
        >
          + Add Dimension
        </button>

        {/* Submit */}
        <div className="card">
          <div style={{ display: 'flex', gap: 12 }}>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            <Link to={`/console/trainer/rubrics/${id}`} className="btn btn-secondary">
              Cancel
            </Link>
          </div>
        </div>
      </form>

      <RequestPanel result={result} loading={loading || saving} />
    </div>
  );
}
